import { SectionHeader } from '../components/shared/SectionHeader';
import { CTAStrip } from '../components/shared/CTAStrip';
import {
  Shield,
  Lock,
  Users,
  History,
  HardDrive,
  Key,
  Eye,
  Server,
  RefreshCw,
  AlertTriangle,
  Database,
  Clock
} from 'lucide-react';

const securityPrinciples = [
  {
    icon: Key,
    title: "Least Privilege Access",
    description: "Users only have access to the functions and data they need for their role. No unnecessary permissions granted."
  },
  {
    icon: Users,
    title: "Role-Based Access Control",
    description: "Comprehensive RBAC system with predefined roles for exporters, inspectors, supervisors, and administrators."
  },
  {
    icon: History,
    title: "Complete Audit Logs",
    description: "Every action in the system is logged with timestamp, user, and details for full accountability and traceability."
  },
  {
    icon: HardDrive,
    title: "Secure Document Storage",
    description: "All uploaded documents and generated certificates stored with appropriate access controls and encryption."
  },
  {
    icon: Lock,
    title: "Encryption in Transit",
    description: "All data transmitted between users and the platform is encrypted using industry-standard TLS protocols."
  },
  {
    icon: Eye,
    title: "Controlled Document Access",
    description: "Certificate access restricted to authorized parties with verification capabilities for trade partners."
  }
];

const governanceFeatures = [
  {
    icon: Database,
    title: "Data Retention Policies",
    description: "Configurable retention periods aligned with regulatory requirements for different document types."
  },
  {
    icon: History,
    title: "Access Logging",
    description: "Detailed logs of who accessed what data and when, supporting audit and compliance requirements."
  },
  {
    icon: Server,
    title: "Environment Separation",
    description: "Production, staging, and development environments kept separate to protect live data."
  }
];

const availabilityFeatures = [
  {
    icon: RefreshCw,
    title: "Regular Backups",
    description: "Automated backup schedules with tested restoration procedures to ensure data recovery capability."
  },
  {
    icon: AlertTriangle,
    title: "Monitoring & Alerts",
    description: "Continuous system monitoring with alerts for performance issues, errors, and security events."
  },
  {
    icon: Clock,
    title: "Incident Response",
    description: "Defined procedures for responding to security incidents and service disruptions."
  }
];

export default function SecurityPage() {
  return (
    <div data-testid="security-page">
      {/* Hero */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white text-sm mb-6">
              <Shield className="w-4 h-4 text-[#C8A24A]" />
              Security & Trust
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Security built into 
              <span className="text-[#C8A24A]"> every layer</span>
            </h1>
            <p className="text-lg text-slate-300">
              Certisfy is designed with security as a foundation, not an afterthought. 
              From access controls to audit trails, every feature prioritizes data protection 
              and system integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Security Principles */}
      <section data-testid="security-principles" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            badge="Security Principles"
            title="How we protect your data"
            subtitle="Core security measures that safeguard your certification data and documents"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {securityPrinciples.map((principle, index) => (
              <div key={index} className="card-feature group" data-testid={`security-principle-${index}`}>
                <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center mb-4 group-hover:bg-[#1F7A3A]" style={{ transition: 'background-color 0.3s' }}>
                  <principle.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{principle.title}</h3>
                <p className="text-slate-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Governance */}
      <section data-testid="data-governance" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge-green mb-4 inline-block">Data Governance</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Responsible data management
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Clear policies and practices for how data is stored, accessed, and retained 
                throughout its lifecycle in the system.
              </p>

              <div className="space-y-6">
                {governanceFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#1F7A3A]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                      <p className="text-slate-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-card p-8">
              <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#1F7A3A]" />
                Access Control Matrix
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="py-3 px-2 text-left text-slate-500 font-medium">Role</th>
                      <th className="py-3 px-2 text-center text-slate-500 font-medium">Apply</th>
                      <th className="py-3 px-2 text-center text-slate-500 font-medium">Inspect</th>
                      <th className="py-3 px-2 text-center text-slate-500 font-medium">Approve</th>
                      <th className="py-3 px-2 text-center text-slate-500 font-medium">Admin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="py-3 px-2 font-medium">Exporter</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 font-medium">Inspector</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 font-medium">Supervisor</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-slate-300">○</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 font-medium">Administrator</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                      <td className="py-3 px-2 text-center text-[#1F7A3A]">●</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Availability */}
      <section data-testid="availability" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            badge="Reliability"
            title="Availability mindset"
            subtitle="Systems and processes designed to keep Certisfy running reliably"
          />

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {availabilityFeatures.map((feature, index) => (
              <div key={index} className="card-base text-center" data-testid={`availability-${index}`}>
                <div className="w-16 h-16 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-[#1F7A3A]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTAStrip 
        title="Questions about security?"
        subtitle="Our team can provide detailed information about our security practices and compliance."
        primaryCTA="Contact Us"
        primaryLink="/contact"
      />
    </div>
  );
}
